import React, { useEffect, useState } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { CssBaseline } from '@mui/material';
import PrivateRoute from './PrivateRoute';
import http from './commonFunctions/httpClient';
import DashboardLayout from './Components/Dashboard/DashboardLayout';
import { VendorLoginForm } from './FrontEnd/Login/Login';
import Logout from './FrontEnd/Logout/LogOut';
import { RegisterForm } from './FrontEnd/Login/RegisterForm';
import { VendorProfile } from './FrontEnd/Login/VendorProfile';
import "leaflet/dist/leaflet.css";
import { getLocalValue, LoacalVariables } from './commonFunctions/CommonFunctions';
import { getHomePath } from './commonFunctions/homePath';
import { endSession, getSessionEndReason, markActivity } from './commonFunctions/session';


const App: React.FC = () => {
  const [token, setToken] = useState<string | null>(() => {
    // A session that timed out (or whose token expired) while the app was closed ends now
    const reason = getSessionEndReason();
    if (reason) {
      endSession(reason);
      return null;
    }
    return localStorage.getItem('token');
  });
  const [isAccessToLogin, setIsAccessToLogin] = useState(false); // fixed typo
  const tokenFromStorage = localStorage.getItem('token');

  if (tokenFromStorage) {
    http.setAuthToken(tokenFromStorage);
  }
  useEffect(() => {
    if (token) {
      http.setAuthToken(token);
    }
  }, [token]);

  // Idle timeout (config.sessionTimeOut minutes) and token expiry, shared across tabs
  useEffect(() => {
    if (!token) return;

    // Opening the app counts as activity
    markActivity(true);
    const handleActivity = () => markActivity();
    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'wheel'];
    activityEvents.forEach((name) => window.addEventListener(name, handleActivity, { passive: true }));

    const checkSession = () => {
      const reason = getSessionEndReason();
      if (reason) endSession(reason);
    };
    const intervalId = setInterval(checkSession, 15000);
    // Background tabs run timers slowly; check as soon as the tab is visible again
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') checkSession();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // Logged out in another tab
    const handleStorage = (e: StorageEvent) => {
      if ((e.key === 'token' || e.key === null) && !localStorage.getItem('token')) {
        window.location.replace('/login');
      }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      clearInterval(intervalId);
      activityEvents.forEach((name) => window.removeEventListener(name, handleActivity));
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('storage', handleStorage);
    };
  }, [token]);

  const handleLogin = (newToken: string) => {
    setToken(newToken);
    localStorage.setItem('token', newToken);
    markActivity(true);
    http.setAuthToken(newToken);
  };

  // Optional logic: set isAccessToLogin based on query param
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('access') === 'login') {
      setIsAccessToLogin(true);
    }
  }, []);

  return (
    <Router>
      <CssBaseline />

      {/* Always redirect to login if access flag is true */}
      {isAccessToLogin && <Navigate to="/login" replace />}

      <Routes>
        {/* Default route redirects based on token */}
        <Route path="/" element={<Navigate to={token ? getHomePath() : "/login"} replace />} />

        {/* Public routes */}
        <Route
          path="/login"
          element={token ? <Navigate to={getHomePath()} replace /> : <VendorLoginForm onLogin={handleLogin} />}
        />
        <Route path="/register" element={<RegisterForm />} />
        <Route path="/register/profile" element={<VendorProfile />} />
        {/* <Route path="/profile/view" element={<VendorProfileView />}/> */}
        <Route path="/logout" element={<Logout />} />

        {/* Protected routes */}
        {/* <Route
          path="/dashboard"
          element={token ? <DashboardLayout /> : <Navigate to="/register" replace />}
        > */}
        <Route path="/" element={<DashboardLayout />}>
          <Route path="*" element={<PrivateRoute token={token} />} />
        </Route>
      </Routes>
    </Router>
  );
};

export default App;
